import {ZoomService} from './zoom.service';
import {fromEvent, interval, Subscription} from 'rxjs';
import {debounce, filter, tap, throttle} from 'rxjs/operators';
import {Injectable} from '@angular/core';

enum KEY_CODES {
    ZOOM_IN = 'NumpadAdd',
    ZOOM_OUT = 'NumpadSubtract',
    ZOOM_RESET = 'Digit0',
    SEARCH = 'KeyF',
    ZOOM_TO_FIT_LEFT = 'AltLeft',
    ZOOM_TO_FIT_RIGHT = 'AltRight',
}

enum NODE_KEY_CODES {
    COPY_KEY = 'KeyC',
    PAST_KEY = 'KeyV',
}

@Injectable({
    providedIn: 'root',
})
export class KeyEventsService {
    constructor(
        private zoomService: ZoomService
    ) {
    }

    private keydownSubscription: Subscription;
    private keyupSubscription: Subscription;
    private mouseEnterSubscription: Subscription;
    private mouseLeaveSubscription: Subscription;
    private nodeKeydownSubscription: Subscription;

    private nodeKeyFilter(event: KeyboardEvent) {
        const code: string = event.code;
        return (event.altKey || event.metaKey || event.ctrlKey || event.key === 'Alt') &&
            (Object.values(NODE_KEY_CODES) as string[]).includes(code)
    }

    private keyFilter(event: KeyboardEvent) {
        const code: string = event.code;
        if (
            (event.altKey || event.metaKey || event.ctrlKey || event.key === 'Alt') &&
            (Object.values(KEY_CODES) as string[]).includes(code)
        ) {
            return true;
        } else if (code === KEY_CODES.ZOOM_IN || code === KEY_CODES.ZOOM_OUT) {
            return true;
        }
        return false;
    }

    public setKeyFunctionality(element, dragAreaId, drawAreaId, nodeEvent, searchInputId) {
        this.globalKeydownSubscribe(dragAreaId, drawAreaId, searchInputId);
        this.globalKeyupSubscribe(dragAreaId, drawAreaId, nodeEvent);
    }

    public globalKeydownSubscribe(dragAreaId, drawAreaId, searchInputId) {

        const keydownFromEvent = fromEvent(window, 'keydown');
        const keydownObservable = keydownFromEvent.pipe(
            filter((event: KeyboardEvent) => this.keyFilter(event)),
            tap((event: KeyboardEvent) => event.preventDefault()),
            throttle(() => interval(50))
        );

        this.keydownSubscription = keydownObservable.subscribe((event: KeyboardEvent) => {
            if (
                (event.altKey && event.code === KEY_CODES.ZOOM_TO_FIT_LEFT) ||
                event.code === KEY_CODES.ZOOM_TO_FIT_RIGHT
            ) {
                const drawArea = document.getElementById(drawAreaId) as HTMLDivElement;
                const dragArea = document.getElementById(dragAreaId) as HTMLDivElement;
                if (drawArea && dragArea) {
                    this.zoomService.zoomToFit(drawArea, dragArea);
                }
            } else if (event.ctrlKey || event.metaKey) {
                this.globalCtrlShortcuts(event, searchInputId);
            } else {
                this.normalShortcuts(event);
            }
        });

    }

    public globalKeyupSubscribe(dragAreaId, drawAreaId, nodeEvent) {
        const keyupFromEvent = fromEvent(window, 'keyup');
        const keyupObservable = keyupFromEvent.pipe(
            filter((event: KeyboardEvent) => this.keyFilter(event)),
            tap((event: KeyboardEvent) => event.preventDefault()),
            throttle(() => interval(50)),
            filter((event: KeyboardEvent) => event.key === 'Alt'),
            tap(() => {
                const dragArea = document.getElementById(dragAreaId) as HTMLDivElement;
                if (dragArea && nodeEvent) {
                    this.zoomService.restorePreviousZoomPercentage(dragArea, nodeEvent.stepId);
                } else {
                    this.zoomService.resetZoom()
                }
            })
        );

        this.keyupSubscription = keyupObservable.subscribe();
    }

    public nodeKeydownSubscribe(copyCallback, pastCallback) {
        const keydownFromEvent = fromEvent(window, 'keydown');
        const keydownObservable = keydownFromEvent.pipe(
            tap(console.warn),
            filter((event: KeyboardEvent) => this.nodeKeyFilter(event)),
            tap((event: KeyboardEvent) => event.preventDefault()),
            debounce(() => interval(100)),
            filter((event: KeyboardEvent) => event.ctrlKey || event.metaKey),
            tap((event: KeyboardEvent) => this.nodeCtrlShortcuts(event, copyCallback, pastCallback))

        );

        this.nodeKeydownSubscription = keydownObservable.subscribe();
    }

    private normalShortcuts(event: KeyboardEvent) {
        switch (event.code) {
            case KEY_CODES.ZOOM_IN:
                this.zoomService.increaseZoom();
                break;
            case KEY_CODES.ZOOM_OUT:
                this.zoomService.decreaseZoom();
                break;
        }
    }

    private globalCtrlShortcuts(event: KeyboardEvent, elementId) {
        switch (event.code) {
            case KEY_CODES.ZOOM_RESET:
                this.zoomService.resetZoom();
                break;
            case KEY_CODES.SEARCH:
                document.getElementById(elementId).click();
                break;

        }
    }

    private nodeCtrlShortcuts(event: KeyboardEvent, copyCallback, pastCallback) {
        switch (event.code) {
            case NODE_KEY_CODES.COPY_KEY:
                if (copyCallback) {
                    copyCallback();
                }
                break;
            case NODE_KEY_CODES.PAST_KEY:
                if (pastCallback) {
                    pastCallback();
                }
                break;
        }
    }

    public unsubscribeKeyEventsSubscriptions() {
        if (this.keydownSubscription) {
            this.keydownSubscription.unsubscribe();
        }
        if (this.keyupSubscription) {
            this.keyupSubscription.unsubscribe();
        }
        if (this.mouseEnterSubscription) {
            this.mouseEnterSubscription.unsubscribe();
        }
        if (this.mouseLeaveSubscription) {
            this.mouseLeaveSubscription.unsubscribe();
        }
        if (this.nodeKeydownSubscription) {
            this.nodeKeydownSubscription.unsubscribe();
        }
    }

    public nodeUnsubscribeKeyEventsSubscriptions() {
        if (this.nodeKeydownSubscription) {
            this.nodeKeydownSubscription.unsubscribe();
        }
    }
}
